import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { SimplePieChart } from '@/components/charts/SimplePieChart'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TrafficLightDot } from '@/components/PostureIndicator'
import { Skeleton } from '@/components/ui/skeleton'
import { TRAFFIC } from '@/lib/trafficLight'

const STATUS_LABELS = {
  green: 'OK',
  amber: 'Review',
  red: 'Action needed',
  grey: 'No data',
}

const STATUS_COLOURS = {
  green: '#10b981',
  amber: '#f59e0b',
  red: '#ef4444',
  grey: '#94a3b8',
}

function combineSummaries(serverSummary, nasSummary) {
  const server = serverSummary || {}
  const nas = nasSummary || {}
  const pass = Number(server.pass_hosts || 0) + Number(nas.pass_hosts || 0)
  const warn = Number(server.warn_hosts || 0) + Number(nas.warn_hosts || 0)
  const fail = Number(server.fail_hosts || 0) + Number(nas.fail_hosts || 0)
  const noData = Number(server.no_data_hosts || 0) + Number(nas.no_data_hosts || 0)
  const configured =
    Number(server.configured_hosts || 0) + Number(nas.configured_hosts || 0)
  const accounted = pass + warn + fail + noData
  const remainder = Math.max(0, configured - accounted)
  return {
    configured,
    serverConfigured: Number(server.configured_hosts || 0),
    nasConfigured: Number(nas.configured_hosts || 0),
    counts: {
      green: pass,
      amber: warn,
      red: fail,
      grey: noData + remainder,
    },
  }
}

function overallFromCounts(counts) {
  if (!counts) return 'grey'
  if (counts.red > 0) return 'red'
  if (counts.amber > 0) return 'amber'
  if (counts.green > 0) return 'green'
  return 'grey'
}

function headline(counts, configured) {
  if (!configured) return 'No backup hosts configured yet'
  if (counts?.red > 0) {
    return `${counts.red} host${counts.red === 1 ? '' : 's'} need backup action`
  }
  if (counts?.amber > 0) {
    return `${counts.amber} host${counts.amber === 1 ? '' : 's'} need backup review`
  }
  if (counts?.green > 0) {
    return `All scored backup hosts look healthy`
  }
  return 'No backup assessment data yet'
}

function countsToPieData(counts) {
  return ['green', 'amber', 'red', 'grey']
    .map((status) => ({
      label: STATUS_LABELS[status],
      value: Number(counts?.[status] || 0),
      status,
    }))
    .filter((row) => row.value > 0)
}

export function BackupEstatePie({
  serverSummary,
  nasSummary,
  loading,
  selectedFilter = null,
  onSelectFilter,
}) {
  const combined = useMemo(
    () => combineSummaries(serverSummary, nasSummary),
    [serverSummary, nasSummary],
  )
  const pieData = useMemo(() => countsToPieData(combined.counts), [combined.counts])
  const overall = overallFromCounts(combined.counts)
  const traffic = TRAFFIC[overall] ?? TRAFFIC.grey
  const activeLabel =
    selectedFilter?.chart === 'backup' ? selectedFilter.key : null

  function handleSliceClick(label) {
    onSelectFilter?.({ chart: 'backup', key: label })
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
          <Skeleton className="mt-2 h-4 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[180px] w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-3">
          <TrafficLightDot status={overall} className="h-4 w-4" />
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base">Server/NAS Backup</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {headline(combined.counts, combined.configured)}
            </p>
          </div>
          {overall !== 'grey' && combined.configured > 0 ? (
            <Badge variant={traffic.badge} className="shrink-0">
              {traffic.label}
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <SimplePieChart
          data={pieData}
          emptyMessage="No backup hosts configured"
          colourFor={(entry) => STATUS_COLOURS[entry.status] || STATUS_COLOURS.grey}
          heightClassName="h-[180px]"
          valueNoun="hosts"
          activeLabel={activeLabel}
          onItemClick={onSelectFilter ? handleSliceClick : undefined}
        />
        <p className="text-xs text-muted-foreground">
          {combined.configured} configured host
          {combined.configured === 1 ? '' : 's'}
          {combined.configured > 0
            ? ` · ${combined.serverConfigured} server · ${combined.nasConfigured} NAS`
            : null}
          .{' '}
          {onSelectFilter ? 'Click a slice to list hosts. ' : null}
          <Link to="/backup" className="text-primary hover:underline">
            Server Backup
          </Link>
          {' · '}
          <Link to="/nas" className="text-primary hover:underline">
            NAS Backup
          </Link>
        </p>
      </CardContent>
    </Card>
  )
}
